import type { FunctionComponent } from 'react';
import { useMemo, useEffect, useRef } from 'react';

import { HostedFieldAction, isFieldMessageEvent } from '@endeavour/payment-integration';

import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { useProviderIntegration } from '../../util/provider-context';

import styles from './hosted-field.module.scss';

const style = createStyleHelper(styles, 'hosted-field');

export interface IHostedFieldProps {
    className?: string;
    providerId: string;
    placeholder?: string;
    name: string;
    integrationId: string;
    value: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

export const HostedField: FunctionComponent<IHostedFieldProps> = ({
    className,
    name,
    value,
    onChange,
    onFocus,
    onBlur,
    integrationId,
    placeholder,
    providerId,
}) => {
    const ref = useRef<HTMLIFrameElement | null>(null);
    const integration = useProviderIntegration();
    const sourceDoc = useMemo(
        () =>
            integration.getHostedField?.({
                providerId,
                placeholder,
                integrationId,
                name,
            }),
        [providerId, placeholder, name, integration, integrationId],
    );

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (!isFieldMessageEvent(event)) {
                return;
            }
            const { data } = event;
            const { action, name: fieldName } = data;

            if (name !== fieldName) {
                return;
            }

            switch (action) {
                case HostedFieldAction.FOCUS:
                    onFocus?.();
                    break;
                case HostedFieldAction.BLUR:
                    onBlur?.();
                    break;
                case HostedFieldAction.INPUT:
                    onChange(data.value);
                    break;
                case HostedFieldAction.ERROR:
                    if (!value) {
                        // Ignore error if no value has ever been inputted.
                        return;
                    }

                    onChange('');
                    break;
                case HostedFieldAction.SUBMIT: {
                    // Since we're using react-hook-form we have to actually trigger a
                    // form submission on the closest form.
                    if (!ref.current) {
                        return;
                    }

                    const form = ref.current.closest('form');

                    if (form) {
                        const submitEvent = new CustomEvent('submit', {
                            bubbles: true,
                            cancelable: true,
                        });
                        form.dispatchEvent(submitEvent);
                    }
                    break;
                }
            }
        };

        window.addEventListener('message', onMessage);

        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [onChange, onFocus, onBlur, name, value]);

    return (
        <iframe ref={ref} className={createClassName([style(), className])} srcDoc={sourceDoc} />
    );
};
